/**
 * Thin wrapper around sessionStorage / localStorage.
 *
 * Centralises key names so they're never string-scattered across the codebase.
 * Also handles JSON serialisation/deserialisation safely.
 *
 * IMPORTANT: This module is for PERSISTENCE only (survive page refresh).
 * Runtime state should live in React context (AppContext).
 */

// ── Key constants ───────────────────────────────────────────────────

export const STORAGE_KEYS = {
  // App context (persisted to sessionStorage)
  CURRENCY: 'currency',
  COUNTRY: 'country',
  LOCALE: 'locale',
  CHANNEL_ID: 'channelId',
  CHANNEL_NAME: 'channelName',
  STORE_KEY: 'storeKey',
  STORE_NAME: 'storeName',
  CUSTOMER_GROUP_ID: 'customerGroupId',
  CUSTOMER_GROUP_NAME: 'customerGroupName',

  // Cart
  CART_ID: 'cartId',
  CART_RESOLVED: 'cartResolved',
  ORDER_ID: 'orderId',
  PRODUCT_STORE_MAP: 'productStoreMap',

  // Auth (Ping)
  PING_ACCESS_TOKEN: 'ping_access_token',
  PING_USER_INFO: 'ping_user_info',
  PING_CODE_VERIFIER: 'ping_code_verifier',
  JUST_LOGGED_IN: 'justLoggedIn',
  POST_LOGIN_REDIRECT: 'postLoginRedirect',

  // Error
  ERROR: 'error',

  // CT client credentials (localStorage)
  CT_ACCESS_TOKEN: 'ct_access_token',
  CT_TOKEN_EXPIRY: 'ct_token_expiry',
  CT_TOKEN_TYPE: 'ct_token_type',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

// ── Helpers ─────────────────────────────────────────────────────────

/** Read a string value from sessionStorage (with optional localStorage fallback). */
export function getSession(key: StorageKey, fallbackToLocal = false): string | null {
  return sessionStorage.getItem(key) || (fallbackToLocal ? localStorage.getItem(key) : null);
}

/** Write a string value to sessionStorage. */
export function setSession(key: StorageKey, value: string): void {
  sessionStorage.setItem(key, value);
}

/** Remove a key from sessionStorage. */
export function removeSession(key: StorageKey): void {
  sessionStorage.removeItem(key);
}

/** Read + parse JSON from sessionStorage. Returns null on failure. */
export function getSessionJSON<T = unknown>(key: StorageKey, fallbackToLocal = false): T | null {
  const raw = getSession(key, fallbackToLocal);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/** Stringify + write JSON to sessionStorage. */
export function setSessionJSON(key: StorageKey, value: unknown): void {
  sessionStorage.setItem(key, JSON.stringify(value));
}

/** Persist a context value to sessionStorage whenever it changes. */
export function syncToStorage(key: StorageKey, value: string | null): void {
  try {
    if (value != null) {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (_error) {
    // Graceful degradation — app continues working without persisted storage
  }
}
